/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQueryClient } from '@tanstack/react-query';
import { LeaseService } from '../services/lease-service';
import { leasesKeys } from './leases-keys';

export interface AccrualPeriodActions {
  recordReceipt: (period: string, amountCents: number, receivedOn: string) => Promise<void>;
  writeOff: (period: string, writeOffDate: string, notes: string) => Promise<void>;
  confirm: (period: string, amountCents?: number) => Promise<void>;
  reverse: (period: string, receiptId: string) => Promise<void>;
}

/**
 * The write side of one lease's months: recebi, baixa, confirmar, estorno.
 *
 * Every action waits for the panel to refetch before resolving, so the
 * dialog closes onto the new numbers rather than onto the old ones.
 *
 * @param agencyId - The owning agency.
 * @param leaseId - The lease whose months these are.
 * @returns One async function per action; each rejects with the API error.
 */
export function useAccrualPeriodActions(agencyId: string, leaseId: string): AccrualPeriodActions {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: leasesKeys.all(agencyId) });

  return {
    recordReceipt: async (period, amountCents, receivedOn) => {
      await LeaseService.recordReceipt(agencyId, leaseId, period, {
        amount_cents: amountCents,
        received_on: receivedOn,
      });
      await refresh();
    },
    writeOff: async (period, writeOffDate, notes) => {
      await LeaseService.writeOff(agencyId, leaseId, period, {
        write_off_date: writeOffDate,
        notes,
      });
      await refresh();
    },
    confirm: async (period, amountCents) => {
      await LeaseService.confirmExpectation(
        agencyId, leaseId, period,
        amountCents === undefined ? {} : { amount_cents: amountCents },
      );
      await refresh();
    },
    reverse: async (period, receiptId) => {
      await LeaseService.reverseReceipt(agencyId, leaseId, period, receiptId);
      await refresh();
    },
  };
}
